/**
 * Shared, isomorphic constants for the Altamont Video Application: limits, allow-lists, Firestore
 * document/collection ids, and the seed content used the first time settings are created.
 */

import type { AdminRole, AiProvider, EditableAppSettings } from './types';

/** Human-readable product name. Candidate-facing copy always calls it a Video Application. */
export const APP_NAME = 'Altamont Video Application';

/* ------------------------------------------------------------------------------------------------
 * Durations and limits
 * ---------------------------------------------------------------------------------------------- */

/** Shortest recording we will accept as a real answer, in seconds. */
export const MIN_ANSWER_SECONDS = 3;

/** Default per-question maximum answer length, in seconds (5 minutes). */
export const DEFAULT_MAX_DURATION_SECONDS = 300;

/** Largest per-question maximum an admin may configure, in seconds (15 minutes). */
export const MAX_ANSWER_DURATION_SECONDS = 900;

/** Smallest per-question maximum an admin may configure, in seconds. */
export const MIN_ANSWER_DURATION_SECONDS = 30;

/** Maximum accepted resume upload size, in bytes (10 MiB). */
export const RESUME_MAX_BYTES = 10 * 1024 * 1024;

/** Maximum accepted answer video size, in bytes (1 GiB). */
export const VIDEO_MAX_BYTES = 1024 * 1024 * 1024;

/** Maximum length of a candidate's full name, in characters. */
export const MAX_NAME_LENGTH = 120;

/** Maximum length of an email address, in characters (the RFC 5321 limit on a forward path). */
export const MAX_EMAIL_LENGTH = 254;

/** Maximum length of the note HR writes when archiving an application, in characters. */
export const MAX_ARCHIVE_REASON_LENGTH = 300;

/** Maximum length of the candidate-facing text of a question, in characters. */
export const MAX_QUESTION_TEXT_LENGTH = 600;

/** Maximum number of work-interest categories a candidate may select. */
export const MAX_INTERESTS = 12;

/* ------------------------------------------------------------------------------------------------
 * Resume upload allow-lists
 * ---------------------------------------------------------------------------------------------- */

/** File extensions accepted for a resume upload, lower-case and without a leading dot. */
export const RESUME_ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'] as const;

/** One of the resume file extensions in {@link RESUME_ALLOWED_EXTENSIONS}. */
export type ResumeExtension = (typeof RESUME_ALLOWED_EXTENSIONS)[number];

/**
 * Accepted resume MIME types mapped to their canonical file extension. `image/jpeg` canonicalises
 * to `jpg` even though `jpeg` is also an accepted extension.
 */
export const RESUME_ALLOWED_MIME = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'image/jpeg': 'jpg',
  'image/png': 'png',
} as const satisfies Readonly<Record<string, ResumeExtension>>;

/** One of the resume MIME types accepted by {@link RESUME_ALLOWED_MIME}. */
export type ResumeMimeType = keyof typeof RESUME_ALLOWED_MIME;

/**
 * A file-signature rule: `prefix` bytes must appear at `offset` for the upload to be treated as
 * the given `ext` / `mime`.
 */
export interface MagicByteSignature {
  readonly ext: ResumeExtension;
  readonly mime: ResumeMimeType;
  readonly prefix: readonly number[];
  readonly offset: number;
}

/**
 * Content-sniffing table used server-side to confirm that uploaded resume bytes really are the
 * declared type. Covers `%PDF`, the OLE2 compound-file header, the ZIP header used by DOCX, the
 * JPEG SOI marker, and the 8-byte PNG signature.
 */
export const RESUME_MAGIC_BYTES: readonly MagicByteSignature[] = [
  // "%PDF"
  { ext: 'pdf', mime: 'application/pdf', prefix: [0x25, 0x50, 0x44, 0x46], offset: 0 },
  // OLE2 compound file (legacy .doc): D0 CF 11 E0 A1 B1 1A E1
  {
    ext: 'doc',
    mime: 'application/msword',
    prefix: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1],
    offset: 0,
  },
  // ZIP local file header "PK\x03\x04" (OOXML .docx)
  {
    ext: 'docx',
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    prefix: [0x50, 0x4b, 0x03, 0x04],
    offset: 0,
  },
  // JPEG SOI + marker
  { ext: 'jpg', mime: 'image/jpeg', prefix: [0xff, 0xd8, 0xff], offset: 0 },
  { ext: 'jpeg', mime: 'image/jpeg', prefix: [0xff, 0xd8, 0xff], offset: 0 },
  // PNG signature
  {
    ext: 'png',
    mime: 'image/png',
    prefix: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
    offset: 0,
  },
] as const;

/* ------------------------------------------------------------------------------------------------
 * Firestore ids and paths
 * ---------------------------------------------------------------------------------------------- */

/**
 * Firebase Storage prefix every brand logo is written under. `AppSettings.logoPath` is refused
 * unless it starts with this, so a logo can never be pointed at a candidate's resume.
 */
export const LOGO_PATH_PREFIX = 'branding/';

/** Document id of the singleton app-settings document inside `/settings`. */
export const SETTINGS_DOC_ID = 'app';

/** Document id of the singleton global-videos document inside `/settings`. */
export const GLOBAL_VIDEOS_DOC_ID = 'globalVideos';

/** Firestore collection paths, so no module has to spell a collection name by hand. */
export const COLLECTIONS = {
  settings: 'settings',
  workInterestCategories: 'workInterestCategories',
  questions: 'questions',
  media: 'media',
  applications: 'applications',
  auditEvents: 'auditEvents',
  adminUsers: 'adminUsers',
} as const;

/* ------------------------------------------------------------------------------------------------
 * Roles
 * ---------------------------------------------------------------------------------------------- */

/** Admin roles from least to most privileged; the index in this tuple is the privilege level. */
export const ROLE_ORDER = ['read', 'write', 'admin'] as const satisfies readonly AdminRole[];

/* ------------------------------------------------------------------------------------------------
 * Seed content
 * ---------------------------------------------------------------------------------------------- */

/** The eight starter work-interest categories, in the order HR should first see them. */
export const DEFAULT_INTEREST_LABELS = [
  'Production / Manufacturing',
  'Machining / CNC',
  'Finishing / Engraving',
  'Shipping / Warehouse',
  'Maintenance',
  'Customer Service / Office',
  'Management / Professional',
  "I'm open to anything",
] as const;

/**
 * Seed values for `/settings/app`, written the first time the settings document is created. Every
 * value here is admin-editable afterwards; the HR contact details are placeholders.
 *
 * The copy that mentions the person on camera is written for the default `hostName`, so it reads
 * properly before anyone has branded their host. A client who names theirs edits this copy in
 * Settings at the same time. The brand colours are the navy and orange of the design system, and a
 * client who has no logo yet simply has none.
 */
export const DEFAULT_SETTINGS: EditableAppSettings = {
  applicationEnabled: true,
  disabledMessage:
    "We're not taking new video applications right now. Please check back soon — we'd still like to hear from you.",

  hostName: 'Your host',
  hostTitle: 'HR Director',
  logoPath: null,
  logoUpdatedAt: null,
  brandPrimaryColor: '#0E1C33',
  brandAccentColor: '#F26A21',
  showHelpText: true,

  welcomeHeadline: 'Altamont wants to meet you.',
  welcomeBody:
    'This is your Video Application. Your host will say hello and ask you a few questions. You answer by recording short videos, right here on your phone or computer. There are no trick questions — just tell us about yourself in your own words.',
  expectedDurationText: 'Most people finish in about 10 to 15 minutes.',
  generalInstructions:
    'Find a quiet spot with decent light and make sure your face is visible. Take all the time you need to think before you start each answer. You can record an answer again as many times as you like — only the answer you keep becomes part of your application.',

  hrContactName: 'Altamont HR (placeholder — update in Admin Settings)',
  hrContactEmail: 'hr@example.com',
  accommodationsText:
    'Need help recording, or need to apply a different way? Email Altamont HR and a person will write back and help you. You can also reach out if you have questions about your application or want it removed.',

  disclosureTitle: 'Before you start recording',
  disclosureBody:
    "Here's exactly what happens with your video application. Please read this, then check the box below to continue.",
  disclosureBullets: [
    'Your answers are recorded as video and audio.',
    'AI may create a written transcript of what you say.',
    'AI may create a short written summary of what you say.',
    'AI does not judge your appearance, your expressions, or your behavior on camera.',
    'AI does not make hiring decisions.',
    'People at Altamont make every employment decision.',
    'You may contact Altamont HR if you need an accommodation, or to ask that your application be deleted.',
  ],
  consentCheckboxLabel: 'I have read this and I agree to record my video application.',
  consentVersion: '2026-09-03.1',

  confirmationHeadline: 'Thank you — we have your video application.',
  confirmationBody:
    "Your answers are safely with us. Someone from Altamont will reach out if there's a potential fit for the kinds of work you're interested in. You can close this page now.",

  defaultMaxDurationSeconds: DEFAULT_MAX_DURATION_SECONDS,

  aiProvider: 'anthropic',
};

/**
 * The providers HR can choose between, with the label shown in Settings and the environment
 * variable that must hold that provider's key. Order is the order they are offered in.
 *
 * Adding one here is not enough on its own: `packages/server/src/summary.ts` has to learn how to
 * call it, and whatever it is must be held to the same system prompt and the same three inputs as
 * the other two. See `docs/AI-DISCLOSURE.md` — the promise Altamont publishes to candidates does
 * not have a per-vendor version.
 */
export const AI_PROVIDERS = [
  { value: 'anthropic', label: 'Anthropic (Claude)', keyEnvVar: 'ANTHROPIC_API_KEY' },
  { value: 'openai', label: 'OpenAI (GPT)', keyEnvVar: 'OPENAI_API_KEY' },
] as const satisfies readonly { value: AiProvider; label: string; keyEnvVar: string }[];

/** The provider used when the stored value is missing or unrecognised. */
export const DEFAULT_AI_PROVIDER: AiProvider = 'anthropic';

/** True when `value` is a provider this build knows how to call. */
export function isAiProvider(value: unknown): value is AiProvider {
  return AI_PROVIDERS.some((provider) => provider.value === value);
}
